import { useMutation, useQuery } from '@tanstack/react-query';
import { ordersAPIService } from '../services/orderAPIService';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logoutCleanup } from '../../../utils/logoutCleanup';
import { MESSAGE_TYPE, messageManager } from '../../../helper';

// Helper function to handle 401 errors
const handle401Error = (error, dispatch, navigate, endpointType, onError) => {
  const statusCode = error?.response?.status;

  if (statusCode === 401) {
    const handleLogout = () => {
      logoutCleanup({ dispatch, navigate, endpointType });
    };
    handleLogout();
    messageManager({
      type: MESSAGE_TYPE.warning,
      messageShow: 'Please login again!'
    });
  } else if (onError) {
    onError(error?.response?.data || error);
  }
};

export const useCreateOrder = (onSuccess = () => {}, onError, endpointType) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { mutateAsync, isLoading, isSuccess, data, isError, error, reset } = useMutation(
    (payload) => ordersAPIService.createOrder(JSON.stringify(payload)),
    {
      onSuccess: (data) => {
        onSuccess(data);
      },
      onError: (e) => {
        console.error('Order Not Created Error', e);
        handle401Error(e, dispatch, navigate, endpointType, onError);
      }
    }
  );

  return {
    createOrder: mutateAsync,
    isCreatingOrder: isLoading,
    isSuccess,
    data,
    isOrderError: isError,
    orderError: error,
    resetOrder: reset
  };
};

export const useGetOrders = (onSuccess, onError, endpointType) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { mutateAsync, data, isLoading, error, refetch } = useMutation(
    (payload) => ordersAPIService.getOrders(payload),
    {
      onSuccess: (data) => {
        onSuccess(data);
      },
      onError: (e) => {
        console.error(e);
        handle401Error(e, dispatch, navigate, endpointType, onError);
      }
    }
  );

  return {
    getOrders: mutateAsync,
    orders: data,
    isLoading,
    refetch,
    error
  };
};

export const useGetOrder = (onSuccess, onError, endpointType) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { mutateAsync, data, isLoading, error, refetch } = useMutation(
    (payload) => ordersAPIService.getOrder(payload),
    {
      onSuccess: (data) => {
        onSuccess(data);
      },
      onError: (e) => {
        console.error(e);
        handle401Error(e, dispatch, navigate, endpointType, onError);
      }
    }
  );

  return {
    getOrderDetails: mutateAsync,
    order: data,
    isLoading,
    refetch,
    error
  };
};

export const useDeleteQuote = (onSuccess = () => {}, onError, endpointType) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { mutateAsync, isLoading, isSuccess, data, isError, error } = useMutation(
    (payload) => ordersAPIService.deleteQuote(payload),
    {
      onSuccess: (data) => {
        onSuccess(data);
      },
      onError: (e) => {
        console.error(e);
        handle401Error(e, dispatch, navigate, endpointType, onError);
      }
    }
  );

  return {
    deleteQuote: mutateAsync,
    isDeletingQuote: isLoading,
    isSuccess,
    data,
    isError,
    error
  };
};

export const useDeleteOrder = (onSuccess = () => {}, onError = () => {}, endpointType) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { mutateAsync, isLoading, isSuccess, data, isError, error } = useMutation(
    (payload) => ordersAPIService.deleteOrder(payload),
    {
      onSuccess: (data) => {
        onSuccess(data);
      },
      onError: (e) => {
        console.error(e);
        handle401Error(e, dispatch, navigate, endpointType, onError);
      }
    }
  );

  return {
    deleteOrder: mutateAsync,
    isDeletingOrder: isLoading,
    isSuccess,
    data,
    isError,
    error
  };
};

export const useUpdateOrder = (onSuccess = () => {}, onError, endpointType) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { mutateAsync, isLoading, isSuccess, data, isError, error } = useMutation(
    (payload) => ordersAPIService.updateOrder(payload),
    {
      onSuccess: (data) => {
        onSuccess(data);
      },
      onError: (e) => {
        console.error(e);
        handle401Error(e, dispatch, navigate, endpointType, onError);
      }
    }
  );

  return {
    updateOrder: mutateAsync,
    isUpdatingOrder: isLoading,
    isSuccess,
    data,
    isError,
    error
  };
};

export const useGetAttributes = (onSuccess = () => {}, onError, endpointType) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { mutateAsync, isLoading, isSuccess, data, isError, error } = useMutation(
    (payload) => ordersAPIService.getAttributes(),
    {
      onSuccess: (data) => {
        onSuccess(data);
      },
      onError: (e) => {
        console.error(e);
        handle401Error(e, dispatch, navigate, endpointType, onError);
      }
    }
  );

  return {
    getAttributes: mutateAsync,
    isGettingAttributes: isLoading,
    isSuccess,
    data,
    isError,
    error
  };
};
